import React, { FC, memo, useMemo } from 'react';
import { useTranslate } from '@/translations';
import { PassportsTransactionData } from '@/types';
import { timeConverter } from '@/utils/helpers';
import { CustomColumns, CustomTable } from '../../CustomTable';
import { Map } from '../../Map';
import { FORMAT_FULL_TIME_DAYJS } from '@/utils/constants';

interface PassportTransactionProps {
  transactions?: PassportsTransactionData[];
}
export const PassportTransactions: FC<PassportTransactionProps> = memo(
  ({ transactions }) => {
    const { t } = useTranslate();

    const columns: CustomColumns<PassportsTransactionData> = useMemo(
      () => [
        {
          dataIndex: 'transDescription',
          title: t('transaction'),
          sortable: true,
          width: 250,
        },
        {
          dataIndex: 'transDate',
          title: t('transactionDate'),
          render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
          width: 250,
          sortable: true,
          defaultSortOrder: 'descend',
        },
        {
          dataIndex: 'transType',
          title: t('transactionCode'),
          sortable: true,
          width: 100,
        },
        {
          dataIndex: 'transVinEndStatus',
          title: t('statusCode'),
          sortable: true,
          width: 100,
        },
        {
          dataIndex: 'transUsername',
          title: t('userName'),
          sortable: true,
          width: 250,
        },
        {
          dataIndex: 'locationDesc',
          title: t('location'),
          sortable: true,
          width: 250,
        },
      ],
      [t],
    );

    return (
      <CustomTable
        dataSource={transactions || []}
        columns={columns}
        rowKey="vehicleTransIndex"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (record) => (
            <TransactionLocationMap transaction={record} />
          ),
          rowExpandable: (record) =>
            record.latitude != null && record.longitude != null,
        }}
      />
    );
  },
);

interface TransactionLocationMapProps {
  transaction: PassportsTransactionData;
}

const TransactionLocationMap: FC<TransactionLocationMapProps> = ({
  transaction,
}) => {
  const location =
    transaction.latitude != null && transaction.longitude != null
      ? {
          lat: transaction.latitude,
          lng: transaction.longitude,
          alt: transaction.altitude,
          acc: transaction.accuracy,
        }
      : undefined;
  const markers = location ? [location] : undefined;
  const circle = location
    ? { center: location, radius: location.acc || 0 }
    : undefined;

  if (!location) return null;

  return (
    <div className="d-flex flex-column" style={{ height: 300 }}>
      <span className="mb-2">
        {`Lat: ${location.lat} | Lng: ${location.lng} | Acc: ${location.acc || 0}m | Alt: ${location.alt || 0}m`}
      </span>
      <div className="flex-fill">
        <Map
          markers={markers}
          circle={circle}
          zoom={14}
          center={location}
          showSiteOverlays={true}
        />
      </div>
    </div>
  );
};
